const moment = require('moment');
const { capitalize, kebabCase, startCase } = require('lodash');
const {
  ALARM_NULL_END_DATE,
  ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY,
  ALERT_STATE_API_TO_UI,
  ALERT_STATE_UI_TO_API,
  SEVERITY_MAP: SEVERITY_MAP_CONST
} = require('@kentik/ui-shared/alerting/constants');
const {
  HEALTH,
  HEALTH_SORT,
  DEFAULT_LOOKBACK_OPTIONS,
  GLOBAL_SITE_ID,
  RE_LAST_CONTEXT_DETAILED_INFO,
  RE_AGENT_ID,
  TASK_TYPES,
  CLOUD_PROVIDER_STATIC_IP_FAMILIES
} = require('@kentik/ui-shared/synthetics/constants');
const pluralize = require('@kentik/ui-shared/util/pluralize');

// this bandage will go when the 'makeAlertManagerAlarm' bandage goes
const SEVERITY_MAP = {
  [HEALTH.CRITICAL]: SEVERITY_MAP_CONST.CRITICAL.ALERT_MANAGER,
  [HEALTH.WARNING]: SEVERITY_MAP_CONST.MAJOR.ALERT_MANAGER, // v2 alarms at warning level come back as major
  major: SEVERITY_MAP_CONST.MAJOR.ALERT_MANAGER
};

/**
 * Per-family static IP status for agent metadata. Explicit `static_ipv4`/`static_ipv6` win; agents
 * still on the legacy combined `static_ip` flag resolve through their cloud provider's families.
 */
function getStaticIpFamilies(metadata = {}) {
  if (metadata.static_ipv4 !== undefined || metadata.static_ipv6 !== undefined) {
    return { v4: !!metadata.static_ipv4, v6: !!metadata.static_ipv6 };
  }

  if (!metadata.static_ip) {
    return { v4: false, v6: false };
  }

  const { cloud_provider } = metadata;
  const families = Object.prototype.hasOwnProperty.call(CLOUD_PROVIDER_STATIC_IP_FAMILIES, cloud_provider)
    ? CLOUD_PROVIDER_STATIC_IP_FAMILIES[cloud_provider]
    : ['v4'];
  return { v4: families.includes('v4'), v6: families.includes('v6') };
}

function isEqualStrings(arg1, arg2) {
  return `${arg1}`.toLowerCase() === `${arg2}`.toLowerCase();
}

/*
  Given a 'legacy' alarm, converts into an alert manager alarm shape
*/
function makeAlertManagerAlarm(legacyAlarm) {
  // first check if it's a v2 alarm as everyone is swimming the same bucket right now
  if (legacyAlarm?.key?.value) {
    return legacyAlarm;
  }

  return {
    ...legacyAlarm,
    start_time: legacyAlarm.startTime,
    end_time: legacyAlarm.endTime,
    severity: SEVERITY_MAP[legacyAlarm.severity] || HEALTH.HEALTHY,
    state: legacyAlarm.state === 'alarm' ? 'ALARM_STATE_ACTIVE' : 'ALARM_STATE_CLEAR',
    key: {
      value: {
        ...(legacyAlarm?.dimensionToKeyPart || {}),
        // fall back and check for a bgp test id if necessary
        testId: legacyAlarm?.dimensionToKeyPart?.test_id || legacyAlarm?.dimensionToKeyPart?.synthetic,
        // agent and target agent id are camelcased in the v2 system
        agentId: legacyAlarm?.dimensionToKeyPart?.agent_id,
        targetAgentId: legacyAlarm?.dimensionToKeyPart?.target_agent_id
      }
    }
  };
}

function getWorstHealthInArray(array, metricName) {
  const filtered = array
    .map((item) => HEALTH_SORT.indexOf(item[metricName] === 'major' ? 'warning' : item[metricName]))
    .filter((value) => value >= 0);
  return HEALTH_SORT[Math.min(...filtered)];
}

/*
  accepts an 'event_start_time' value from an alert manager alarm

  legacy v1 alarms and v2 alarms occurring before the 12/24 release of first triggers
  will not have a value

  v1 alarms will be undefined and v2 alarms will have the ALARM_NULL_END_DATE value
*/
function getFirstTriggerTime(value) {
  if (value && value !== ALARM_NULL_END_DATE) {
    return value;
  }

  return null;
}

/*
  Given a list of complete alarms, returns a shorted list of alarms suitable for comparing against timestamps and organizing on a timeline
  Example result with a single active alarm:

  [
    {
      id: '018f7269-3ba4-7c5b-8fbf-37d8e935a73a',
      severity: 'SEVERITY_CRITICAL',
      state: 'ALARM_STATE_ACTIVE',
      start: 1715611427,
      end: null
    }
  ]
*/
function getTimelineAlarms({ alarms = [] } = {}) {
  return alarms.map((alarm) => {
    // default to v1 alarm datetime, allow fallback to v2, beginning painting at the first trigger timestamp if available
    const startTime = alarm.startTime || getFirstTriggerTime(alarm.event_start_time) || alarm.start_time;
    const endTime = alarm.endTime || alarm.end_time;
    const start = moment(startTime).unix();
    const end = endTime === null || endTime === ALARM_NULL_END_DATE ? null : moment(endTime).unix();

    return {
      id: alarm.id,
      severity: ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY[alarm.severity] || alarm.severity,
      state: ALERT_STATE_UI_TO_API[alarm.state] || alarm.state,
      metric: alarm.key?.value?.metric || alarm.details?.bgp?.display || alarm.details?.type,
      start,
      end,
      alarm: makeAlertManagerAlarm(alarm)
    };
  });
}

/*
  Given a timeline alarm and start and end timestamps, returns an object:

  {
    didAlarm // boolean indicating the alarm started inside the timestamp range
    isWithinRange // boolean indicating the alarm was present inside the timestamp range
  }
*/
function isTimelineAlarmWithinTimestampRange({ timelineAlarm, tsStart, tsEnd } = {}) {
  const result = { didAlarm: false, isWithinRange: false };

  if (timelineAlarm?.start && tsStart) {
    const alarmStartedInsideRange =
      timelineAlarm.start >= +tsStart && ((!!tsEnd && timelineAlarm.start < tsEnd) || !tsEnd);

    const alarmPresentInsideRange =
      timelineAlarm.start <= +tsStart && (timelineAlarm.end > (tsEnd || +tsStart) || timelineAlarm.end === null);

    result.didAlarm = alarmStartedInsideRange;
    result.isWithinRange = alarmStartedInsideRange || alarmPresentInsideRange;
  }

  return result;
}

/*
  provides total counts by severity and metric, displayed as part of the alarm timeline tooltip renderer
*/
function getAlarmSummary(alarms) {
  return alarms.reduce(
    (summary, alarm) => {
      const { severity, metric } = alarm;
      const severityKey = severity === 'major' ? 'warning' : severity;
      const metricName = metric || 'other';

      // update severity and metric counts
      summary.severities[severityKey] = (summary.severities[severityKey] || 0) + 1;
      summary.metrics[metricName] = (summary.metrics[metricName] || 0) + 1;

      // assemble a running count of agent errors if there are any
      (alarm?.alarm?.details?.metrics || []).forEach((issueMetric) => {
        if (isEqualStrings(issueMetric.metric, 'error')) {
          // prefer to show the error details but protect against missing values while still indicating an error state
          const key = issueMetric.value || issueMetric.metric;
          summary.taskErrors[key] = (summary.taskErrors[key] || 0) + 1;
        }
      });

      return summary;
    },
    { severities: {}, metrics: {}, taskErrors: {} }
  );
}

/*
  Given a list of alarms and a list of timestamps, assembles a list representing a health timeline

  ex:

  [
    {
      alarm: true, // boolean indicating whether it fired in the stamp
      alarms: [{
        id: '018fc570-f21d-7a0e-b2a7-3a80073ba042',
        severity: 'major',
        state: 'ALARM_STATE_ACTIVE',
        start: 1717004450,
        end: null
      }], // list of alarms present in the stamp
      health: 'warning', // aggregated worst health of the bunch
      time: 1717004450000 // timestamp
    },
    ...
  ]
*/
function getHealthTimeline({ alarms, timestamps, convertToMs = true }) {
  if (timestamps.length) {
    const timelineAlarms = getTimelineAlarms({ alarms });

    return timestamps.map((ts, index) => {
      let resultAlarmed = false;
      const resultAlarms = [];

      for (let i = 0; i < timelineAlarms.length; i += 1) {
        const alarm = timelineAlarms[i];
        const { didAlarm, isWithinRange } = isTimelineAlarmWithinTimestampRange({
          timelineAlarm: alarm,
          tsStart: ts,
          tsEnd: +timestamps[index + 1]
        });

        if (didAlarm) {
          resultAlarmed = didAlarm;
        }

        if (isWithinRange) {
          resultAlarms.push(alarm);
        }
      }

      return {
        time: convertToMs ? +ts * 1000 : +ts,
        alarm: resultAlarmed,
        alarms: resultAlarms,
        alarmSummary: getAlarmSummary(resultAlarms),
        health: resultAlarms.length ? getWorstHealthInArray(resultAlarms, 'severity') : 'healthy'
      };
    });
  }

  return [];
}

/*
  keys healthtimeline alarms by result time in ms
*/
function getTimelineAlarmsByResultTimeMs(healthTimeline) {
  return (healthTimeline || []).reduce(
    (acc, { time, alarms }) => ({
      ...acc,
      [time]: alarms.map((alarm) => alarm.alarm) // the complete alarm is in the 'alarm' property
    }),
    {}
  );
}

/*
  Checks alarm state in both v1 and v2 styles to determine if it's active
*/
function isAlarmActive(alarmState) {
  return alarmState === ALERT_STATE_UI_TO_API.alarm || alarmState === ALERT_STATE_API_TO_UI.ALARM_STATE_ACTIVE;
}

function getWorstHealth(health1, health2) {
  if (HEALTH_SORT.indexOf(health1) <= HEALTH_SORT.indexOf(health2)) {
    return health1;
  }
  return health2;
}

// Constants for pre-alert and post-clear context buffers
const PRE_ALERT_BUFFER = 1800; // 30 min (first priority for raw data)
const PRE_ALERT_BUFFER_AGGREGATED = 3600; // 1 hour (when falling back to aggregated)
const POST_CLEAR_AGGREGATED = 3600; // 1 hour post-clear in aggregated mode

/**
 * Computes the optimal time window for alert details charts.
 *
 * Priority: (1) pre-alert context, (2) alarm duration, (3) post-clear recovery.
 * Stays within maxRawLookback when possible; falls back to aggregated data for long alarms.
 *
 * @param {Object} params
 * @param {number} params.maxRawLookback - Max query window (seconds) for raw data, from getMaxRawLookbackByTestFreq
 * @param {number} params.alarmFirstTriggerTime - Epoch seconds when the alarm first triggered
 * @param {number|null} params.alarmEndTime - Epoch seconds when the alarm cleared, or null if still active
 * @param {number} params.now - Current time as epoch seconds
 * @returns {{ start_time: number, end_time: number, isAggregated: boolean }}
 */
function getChartTimeRange({ maxRawLookback, alarmFirstTriggerTime, alarmEndTime, now }) {
  // Derive active/cleared from alarmEndTime to avoid contradictory inputs
  const isActive = alarmEndTime == null;

  if (!isActive) {
    // ── Cleared Alarm ──
    const alarmDuration = alarmEndTime - alarmFirstTriggerTime;
    const budgetForAlarmAndPre = PRE_ALERT_BUFFER + alarmDuration;

    if (budgetForAlarmAndPre <= maxRawLookback) {
      // Fits in raw budget — 30min pre-alert + alarm + remaining as post-clear
      const end_time = Math.min(alarmEndTime + (maxRawLookback - budgetForAlarmAndPre), now);
      // If post-clear was clipped at now, reclaim the unused budget as additional pre-alert
      const start_time = end_time - maxRawLookback;
      return { start_time, end_time, isAggregated: false };
    }

    if (alarmDuration <= maxRawLookback) {
      // Alarm fits raw but full pre-alert doesn't — compress pre-alert to fill remaining budget
      const preAlertBudget = maxRawLookback - alarmDuration;
      return {
        start_time: alarmFirstTriggerTime - preAlertBudget,
        end_time: Math.min(alarmEndTime, now),
        isAggregated: false
      };
    }

    // Alarm itself exceeds raw budget — fall back to aggregated with generous context
    return {
      start_time: alarmFirstTriggerTime - PRE_ALERT_BUFFER_AGGREGATED,
      end_time: Math.min(alarmEndTime + POST_CLEAR_AGGREGATED, now),
      isAggregated: true
    };
  }

  // ── Active Alarm ──
  const activeDuration = now - alarmFirstTriggerTime;

  if (PRE_ALERT_BUFFER + activeDuration <= maxRawLookback) {
    // Full 30-min pre-alert fits in raw budget
    return {
      start_time: alarmFirstTriggerTime - PRE_ALERT_BUFFER,
      end_time: now,
      isAggregated: false
    };
  }

  if (activeDuration <= maxRawLookback) {
    // Alarm is growing but still fits raw — compress pre-alert to fill budget
    return {
      start_time: now - maxRawLookback,
      end_time: now,
      isAggregated: false
    };
  }

  // Exceeds raw budget — aggregated with 1hr pre-alert
  return {
    start_time: alarmFirstTriggerTime - PRE_ALERT_BUFFER_AGGREGATED,
    end_time: now,
    isAggregated: true
  };
}

/**
 * Test frequency in seconds: the configured period, falling back to the ping period.
 * @param {{ get: (path: string) => unknown }} test
 * @returns {number | undefined}
 */
function getTestFrequency(test) {
  if (!test || typeof test.get !== 'function') {
    return undefined;
  }
  return Number(test.get('config.period') || test.get('config.ping.period')) || undefined;
}

function getMaxRawLookbackByTestFreq(test) {
  if (!test || typeof test.get !== 'function') {
    return null;
  }

  // attempt to get the test frequency, first checking the configured period, falling back to the ping period
  const config = test.get('config');
  const testFrequency = config?.period || config?.ping?.period;

  // Based off current syngest aggregation algorithm (https://github.com/kentik/syngest/blob/a4b343a9e833314765bb99b88141ddcf3dbb2063/pkg/ds/health_ds.go#L582)
  // conversion: if testFreq < aggregation value, then return queryDuration conditional value
  if (testFrequency) {
    if (testFrequency < 60 /* 1m */) {
      return 900; // 15m
    }
    if (testFrequency < 300 /* 5m */) {
      return 3600; // 1h
    }
    if (testFrequency < 600 /* 10m */) {
      return 10800; // 3h
    }
    if (testFrequency < 1800 /* 30m */) {
      return 21600; // 6h
    }
    if (testFrequency < 10800 /* 3h */) {
      return 86400; // 24h
    }
    if (testFrequency < 21600 /* 6h */) {
      return 604800; // 7d
    }
    // max out at 7 days just to not beat on our system by default?
    return 604800; // 7d
  }
  return null;
}

function getLookbackOptions({ test, lookbackOptions = DEFAULT_LOOKBACK_OPTIONS, maxRawLookback }) {
  const max = maxRawLookback || getMaxRawLookbackByTestFreq(test);

  return lookbackOptions.reduce(
    (acc, option) => {
      if (option.value <= max) {
        acc.raw.push(option);
      } else {
        acc.aggregate.push(option);
      }

      return acc;
    },
    { raw: [], aggregate: [] }
  );
}

// Returns a list of metrics ({ metric, value, severity }) from an issue in the event view model of an alarm
function getIssueMetrics(issue) {
  if (issue?.value) {
    const { DetailedInfo, IssueMetrics } = issue.value;

    if (Array.isArray(IssueMetrics) && IssueMetrics.length > 0) {
      return IssueMetrics.filter(
        (item) =>
          // only keep warning and critical severities
          item?.severity === HEALTH.CRITICAL || item?.severity === HEALTH.WARNING
      );
    }

    if (Array.isArray(DetailedInfo)) {
      // parse the metrics into structured data, keeping only the severities we want to report on
      return (
        DetailedInfo
          // Object.assign will 'upgrade' the null-prototype object that .exec groups returns, allowing things like toString to not bomb in the ui
          .map((info) => Object.assign({}, RE_LAST_CONTEXT_DETAILED_INFO.exec(info)?.groups || {}))
          .filter((item) => item?.severity === HEALTH.CRITICAL || item?.severity === HEALTH.WARNING)
      );
    }
  }

  return [];
}

/*
  Returns an agent id from an alarm's event view model issue
  First checks for a direct property but can try to fallback to parsing from a subtest url to handle now-legacy alarms
*/
function getAgentIdFromIssue(issue) {
  if (issue?.value) {
    const { AgentId, Url } = issue.value;

    if (AgentId) {
      return AgentId;
    }

    if (typeof Url === 'string') {
      const { agentId } = RE_AGENT_ID.exec(Url)?.groups || {};

      if (agentId) {
        return agentId;
      }
    }
  }

  return null;
}

function getGroupFromKey(key) {
  if (key) {
    // LABEL:100 SITE:100 SITE:global are examples
    const [groupType, groupValue] = (key.value?.group || '').split(':');

    if (groupType && groupValue) {
      let groupId = parseInt(groupValue, 10);
      // use the GLOBAL_SITE_ID if we can't numerically parse the group value
      groupId = Number.isFinite(groupId) ? groupId : GLOBAL_SITE_ID;

      // with these groups inside a key value as colon-delimited, we always know they're just one
      return {
        [`${groupType.toLowerCase()}Ids`]: [groupId], // ex: labelIds: [100]
        [`${groupType.toLowerCase()}sGroupLabel`]: `1 ${capitalize(groupType)}` // ex: labelsGroupLabel: '1 Label'
      };
    }
  }

  return {};
}

/*
  Collects groups of data (agentIds, targets, and metrics) from a list of issues, primarily used for rendering summaries in the alarm details table.
  Each of the sub-utils this leverages (getAgentIdFromIssue, getIssueMetrics) contain various fallbacks to handle situations
  where we can report on now-legacy alarms with older notations (think per-subtest alarms with DetailedInfo for metrics)
  as well as 'modern' alarms with new direct properties (AgentId as direct property on an issue) at parity.
*/
function getIssueGroupData({ key, details } = {}) {
  const keyGroup = getGroupFromKey(key);
  const alarmDetails = details || [];
  const group = alarmDetails.find((detail) => detail.name === 'AgentGroup')?.value || null; // grouped alerting percentage label
  const issues = alarmDetails.filter((detail) => detail.tag === 'issue');

  const groupData = issues.reduce(
    (acc, issue) => {
      const agentId = getAgentIdFromIssue(issue);
      const issueMetrics = getIssueMetrics(issue).map(({ metric }) => metric);

      if (agentId) {
        acc.agentIds.add(agentId);
      }

      if (issue?.value?.Target) {
        acc.targets.add(issue.value.Target);

        if (agentId) {
          // log the association that says this agent and issue target are part of this alarm state
          acc.issueTargetsByAgentId[agentId] = new Set([
            ...(acc.issueTargetsByAgentId[agentId] || []),
            issue.value.Target
          ]);
        }
      }

      acc.metrics = new Set([...acc.metrics, ...issueMetrics]);

      return acc;
    },
    { agentIds: new Set(), targets: new Set(), metrics: new Set(), issueTargetsByAgentId: {} }
  );

  const agentIds = Array.from(groupData.agentIds);
  const targets = Array.from(groupData.targets);
  const metrics = Array.from(groupData.metrics);

  return {
    agentIds,
    targets,
    metrics,
    issueTargetsByAgentId: groupData.issueTargetsByAgentId,
    agentsGroupLabel: group || `${agentIds.length} ${pluralize('Agent', agentIds.length)}`,
    targetsGroupLabel: `${targets.length} ${pluralize('Target', targets.length)}`,
    metricsGroupLabel: metrics.join(', '),
    ...keyGroup
  };
}

/*
  Given a response from the alarm trigger list api, expands issues inside triggers to a list of data suitable for representation as AlarmTriggerModels
*/
function expandIssues(response) {
  const { triggers } = response || {};
  const expandedIssues = (triggers || []).reduce((allTriggers, trigger) => {
    const { key, severity, timestamp } = trigger;
    const { details = [] } = trigger?.context?.event_view_model || {};
    const issues = details.filter((detail) => detail.tag === 'issue');
    const groupData = getIssueGroupData({ key, details });

    // attach the severity of the trigger (group) to the group data so we can get an accurate severity rollup in the table group summary
    groupData.severity = severity;

    return allTriggers.concat(
      issues.map((issue) => ({
        agentId: getAgentIdFromIssue(issue),
        severity: issue?.value?.Severity || severity, // favor the severity attached to the issue but fallback to the trigger if necessary
        timestamp,
        issue,
        groupData
      }))
    );
  }, []);

  return { ...response, triggers: expandedIssues };
}

/*
  Given a list of grouped alerting filter ids, returns a new list depending on the asOptionValue setting.
  When asOptionValue is true, filter ids that contain the special GLOBAL_SITE_ID will be a string.
  This is because the current value of the global site id is 0 and there is logic throughout our core
  select components and utils that performs loose equality checks on option values.
  Due to this logic, doing things such as clearing all selected items will result in the global site id showing as a default selection.
  Using this value as a string instead, avoids this result.
*/
function cleanFilterIds({ filterIds = [], asOptionValue = false } = {}) {
  return filterIds.reduce((validFilterIds, filterId) => {
    let numericFilterId = parseInt(filterId, 10);

    if (Number.isFinite(numericFilterId)) {
      // we only keep ids that are parseable to numbers
      if (asOptionValue && numericFilterId === GLOBAL_SITE_ID) {
        // we want the special global site id to be treated as a string.
        // this protects against incorrect default selections due to loose equality checks in core select components/utils
        numericFilterId = `${numericFilterId}`;
      }

      return validFilterIds.concat(numericFilterId);
    }

    return validFilterIds;
  }, []);
}

/*
  Returns a list of valid grouped alert overrides. A valid override:

  1. Has a percent of group that is different from the default percent
  2. Is configured against a metric that is not disabled (globally or individually)
*/
function normalizeGroupedAlertSettings({ groupedAlertSettings = {}, healthSettings = {}, tasks = [] } = {}) {
  // currently unimplemented properties that we want to ship with proper defaults
  const defaultProperties = { countOfSrcGroup: 0, disabled: false };

  if (groupedAlertSettings?.default) {
    groupedAlertSettings.default = { ...groupedAlertSettings.default, ...defaultProperties };

    groupedAlertSettings.default.filterIds = cleanFilterIds({
      filterIds: groupedAlertSettings.default.filterIds || []
    });
  }

  if (groupedAlertSettings?.overrides) {
    const hasPing = tasks.includes(TASK_TYPES.PING);

    groupedAlertSettings.overrides = groupedAlertSettings.overrides.reduce((acc, override) => {
      // health/metric disable checks are falsey
      const allHealthEnabled = !!healthSettings?.healthDisabled === false;
      const isMetricEnabled = !!healthSettings?.disabledMetrics?.[override?.metric] === false;
      const isSupported = !!(!hasPing && override?.metric?.startsWith(TASK_TYPES.PING)) === false;
      const {
        percentOfSrcGroup: defaultPercentage,
        srcGroupBy: defaultSrcGroupBy,
        filterIds: defaultFilterIds = []
      } = groupedAlertSettings?.default || {};
      const isPercentageOverridden = override?.percentOfSrcGroup !== defaultPercentage;

      if (allHealthEnabled && isMetricEnabled && isPercentageOverridden && isSupported) {
        // add the valid, enabled override
        // note the enforcement of all overrides requiring the same srcGroupBy and filterIds as the default
        // we do this here to ensure consistent treatment of data whether coming from the ui or api
        return [
          ...acc,
          { ...override, ...defaultProperties, srcGroupBy: defaultSrcGroupBy, filterIds: defaultFilterIds }
        ];
      }

      return acc;
    }, []);
  }

  return groupedAlertSettings;
}

// returns a string into a capitalized format suitable for labels while uppercasing well-known terms
// this util runs the value through kebabCase to account for any space-delimited strings too
// the match on 'i p' accounts for when cleansing metrics labels such as 'DNS Result IPs' since kebab case will split up the caps
function toLabel(value) {
  return startCase(kebabCase(value)).replace(/(http|dns|i p)/gi, (match) => match.replaceAll(' ', '').toUpperCase());
}

// Helper to escape CSV values
function escapeCsvValue(value) {
  if (value === null || value === undefined) {
    return '';
  }
  const str = String(value);
  if (/[",\r\n]/.test(str)) {
    // Escape double quotes by doubling them
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// Helper to convert the flattened results to CSV
function convertResultsToCsv(results) {
  if (!Array.isArray(results) || results.length === 0) {
    return '';
  }
  const rows = [];
  const headers = Array.from(new Set(results.flatMap((item) => Object.keys(item))));
  rows.push(headers.map(escapeCsvValue));
  results.forEach((item) => {
    const row = headers.map((key) => escapeCsvValue(item[key]));
    rows.push(row);
  });
  return rows.map((row) => row.join(',')).join('\r\n');
}

function getHttpStages(isPageLoad) {
  const durations = [];
  if (isPageLoad) {
    durations.push({ name: 'Navigation Time', key: 'domainLookupStart', stagesKey: 'navigation_time' });
  }
  durations.push(
    { name: 'Domain Lookup Time', key: 'domainLookupEnd', stagesKey: 'domain_lookup_time' },
    { name: 'Connect Time', key: 'connectEnd', stagesKey: 'connect_time' }
  );
  if (isPageLoad) {
    durations.push(
      { name: 'Response Time', key: 'responseEnd', stagesKey: 'response_time' },
      { name: 'DOM Processing Time', key: 'duration', stagesKey: 'dom_processing_time' }
    );
  } else {
    durations.push({ name: 'Response Time', key: 'duration', stagesKey: 'response_time' });
  }
  return durations;
}

module.exports = {
  getHealthTimeline,
  getTimelineAlarmsByResultTimeMs,
  getTimelineAlarms,
  getWorstHealthInArray,
  isAlarmActive,
  isTimelineAlarmWithinTimestampRange,
  makeAlertManagerAlarm,
  getWorstHealth,
  getChartTimeRange,
  PRE_ALERT_BUFFER,
  PRE_ALERT_BUFFER_AGGREGATED,
  POST_CLEAR_AGGREGATED,
  getMaxRawLookbackByTestFreq,
  getTestFrequency,
  getLookbackOptions,
  getFirstTriggerTime,
  getIssueMetrics,
  getAgentIdFromIssue,
  getIssueGroupData,
  expandIssues,
  normalizeGroupedAlertSettings,
  toLabel,
  getGroupFromKey,
  cleanFilterIds,
  convertResultsToCsv,
  isEqualStrings,
  getHttpStages,
  getStaticIpFamilies
};
